import { IOrganization, IOrganizationClauseChecklist } from "@/api";
import { DuplicateClauseChecklistDialog } from "@/components/admin/dialogs/clause-checklists/DuplicateClauseChecklistDialog";
import { Button } from "@/components/shared/buttons/Button";
import { ContentCopy, Delete } from "@mui/icons-material";
import { ListItemIcon, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { useDialogs } from "@toolpad/core";
import React, { useId, useState } from "react";
import { RemoveClauseChecklistFromOrganizationDialog } from "../dialogs/RemoveClauseChecklistFromOrganizationDialog";

interface IMenuActionItem {
    /**
     * The label of the action
     */
    label: string;
    /**
     * The icon of the action
     */
    icon?: React.ReactNode;
    /**
     * Whether the action is disabled
     */
    disabled?: boolean;
    /**
     * Why the action is disabled or leaves part of the selection out
     */
    reason?: string;
    /**
     * The action to perform when the item is clicked
     */
    onClick: () => void;
}

interface IProps {
    /**
     * The selected clause checklist IDs
     */
    selectedClauseChecklistIds: number[];
    /**
     * The organization
     */
    organization: IOrganization;
    /**
     * The rows of the table
     */
    rows: IOrganizationClauseChecklist[];
}

/**
 * The toolbar actions for the organization clause checklists table
 */
export const OrganizationClauseChecklistsActions = ({
    selectedClauseChecklistIds,
    rows,
    organization,
}: IProps) => {
    const dialogs = useDialogs();
    const itemId = useId();

    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const [selectedChecklistToDuplicate, setSelectedChecklistToDuplicate] =
        useState<IOrganizationClauseChecklist | null>(null);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const selectedRows = rows.filter((row) =>
        selectedClauseChecklistIds.includes(row.id)
    );
    const directlyAssignedIds = selectedRows
        .filter((row) => row.is_directly_assigned)
        .map((row) => row.id);
    const skippedCount = selectedRows.length - directlyAssignedIds.length;
    const deleteReason =
        selectedRows.length === 0
            ? "Select a checklist first."
            : directlyAssignedIds.length === 0
              ? "Checklists shared with a group can't be removed here."
              : skippedCount > 0
                ? `${skippedCount} shared with a group won't be removed.`
                : undefined;
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const menuActionItems: IMenuActionItem[] = [
        {
            label: "Delete Selected",
            icon: <Delete fontSize="small" />,
            disabled: directlyAssignedIds.length === 0,
            reason: deleteReason,
            onClick: async () => {
                // Close the menu
                handleClose();

                // Open the destructive dialog
                await dialogs.open(
                    RemoveClauseChecklistFromOrganizationDialog,
                    {
                        organizationId: organization.id,
                        clauseChecklistIds: directlyAssignedIds,
                        skippedCount,
                    }
                );
            },
        },
        {
            label: "Duplicate Selected",
            icon: <ContentCopy fontSize="small" />,
            disabled:
                selectedClauseChecklistIds.length === 0 ||
                selectedClauseChecklistIds.length > 1,
            onClick: () => {
                // Close the menu
                handleClose();

                const checklistToDuplicate = rows.find((row) =>
                    selectedClauseChecklistIds.includes(row.id)
                );

                if (checklistToDuplicate) {
                    setSelectedChecklistToDuplicate(checklistToDuplicate);
                    setDuplicateDialogOpen(true);
                }
            },
        },
    ];

    return (
        <>
            <Button
                type="button"
                variant="contained"
                color="tertiary"
                disableElevation
                sx={{ borderRadius: 22 }}
                onClick={handleClick}
            >
                Actions
            </Button>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        sx: { minWidth: 280, maxWidth: 320, mt: 1.5 },
                        variant: "outlined",
                    },
                    list: { disabledItemsFocusable: true },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
                {menuActionItems.map(
                    ({ onClick, disabled, icon, label, reason }, index) => (
                        <MenuItem
                            key={index}
                            onClick={onClick}
                            disabled={disabled}
                            aria-labelledby={`${itemId}-${index}-label`}
                            aria-describedby={
                                reason ? `${itemId}-${index}-reason` : undefined
                            }
                        >
                            {icon && <ListItemIcon>{icon}</ListItemIcon>}
                            <Stack minWidth={0}>
                                <span id={`${itemId}-${index}-label`}>
                                    {label}
                                </span>
                                {reason && (
                                    <Typography
                                        id={`${itemId}-${index}-reason`}
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ whiteSpace: "normal" }}
                                    >
                                        {reason}
                                    </Typography>
                                )}
                            </Stack>
                        </MenuItem>
                    )
                )}
            </Menu>
            {selectedChecklistToDuplicate && (
                <DuplicateClauseChecklistDialog
                    open={duplicateDialogOpen}
                    onClose={() => {
                        setDuplicateDialogOpen(false);
                        setSelectedChecklistToDuplicate(null);
                    }}
                    sourceChecklist={selectedChecklistToDuplicate}
                    defaultOrganization={organization}
                />
            )}
        </>
    );
};
